import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/daisy';
import { Colors, Sizes, Variants } from '@/components/ui/types';
import { VersioningStatusUIEnum } from '@/enums/VersioningStatus';
import { convertVersioningStatusToUIEnum } from '@/utils/versioning';
import {
  ArrowPathIcon,
  CheckIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ConditionalRenderer from '../ui/custom/ConditionalRenderer';
import getFormatedCoursePathVersion from '@/utils/shared/getFormatedCoursePathVersion';
import CoursePath from '@/models/CoursePath';

type ContPedStatusProps = {
  size?: Sizes;
  showVersion?: boolean;
  showIcon?: boolean;
  coursePath: CoursePath;
};

export default function ContPedStatus({
  size,
  showVersion = false,
  showIcon = true,
  coursePath,
}: ContPedStatusProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courses.status',
  });

  const uiStatus = convertVersioningStatusToUIEnum(
    coursePath?.status,
    coursePath?.version,
  );

  const mapStatusToColor: Record<VersioningStatusUIEnum, Colors> = {
    [VersioningStatusUIEnum.VERSIONING]: 'secondary',
    [VersioningStatusUIEnum.PUBLISHED]: 'success',
    [VersioningStatusUIEnum.EDITING]: 'warning',
    [VersioningStatusUIEnum.BETA]: 'info',
    [VersioningStatusUIEnum.DRAFT]: 'warning',
    [VersioningStatusUIEnum.DISABLED]: 'default',
  };

  const iconClassname = 'w-3.5 h-3.5';
  const mapStatusToIcon: Record<VersioningStatusUIEnum, React.ReactNode> = {
    [VersioningStatusUIEnum.VERSIONING]: (
      <ArrowPathIcon className={iconClassname} />
    ),
    [VersioningStatusUIEnum.PUBLISHED]: <CheckIcon className={iconClassname} />,
    [VersioningStatusUIEnum.EDITING]: (
      <PencilSquareIcon className={iconClassname} />
    ),
    [VersioningStatusUIEnum.BETA]: <ArrowPathIcon className={iconClassname} />,
    [VersioningStatusUIEnum.DRAFT]: <ArrowPathIcon className={iconClassname} />,
    [VersioningStatusUIEnum.DISABLED]: <XMarkIcon className={iconClassname} />,
  };

  const mapStatusToText: Record<VersioningStatusUIEnum, string> = {
    [VersioningStatusUIEnum.VERSIONING]: t('VERSIONING'),
    [VersioningStatusUIEnum.PUBLISHED]: t('PUBLISHED'),
    [VersioningStatusUIEnum.EDITING]: t('EDITING'),
    [VersioningStatusUIEnum.BETA]: t('BETA'),
    [VersioningStatusUIEnum.DISABLED]: t('DISABLED'),
    [VersioningStatusUIEnum.DRAFT]: t('NEW'),
  };

  const mapStatusToVariant: Record<VersioningStatusUIEnum, Variants> = {
    [VersioningStatusUIEnum.VERSIONING]: 'dash',
    [VersioningStatusUIEnum.PUBLISHED]: 'soft',
    [VersioningStatusUIEnum.EDITING]: 'dash',
    [VersioningStatusUIEnum.BETA]: 'soft',
    [VersioningStatusUIEnum.DRAFT]: 'dash',
    [VersioningStatusUIEnum.DISABLED]: 'default',
  };

  return (
    <Badge
      color={mapStatusToColor[uiStatus]}
      variant={mapStatusToVariant[uiStatus]}
      size={size}
      data-testid="cont-ped-status-badge"
    >
      <ConditionalRenderer condition={showIcon}>
        {mapStatusToIcon[uiStatus]}
      </ConditionalRenderer>
      {mapStatusToText[uiStatus]}
      <ConditionalRenderer condition={showVersion}>
        <span>{getFormatedCoursePathVersion({ coursePath })}</span>
      </ConditionalRenderer>
    </Badge>
  );
}
